import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { api } from '../../lib/api';

const SEVERITIES = [
  { value: 'minor', label: 'Minor', bg: 'secondary' },
  { value: 'moderate', label: 'Moderate', bg: 'warning' },
  { value: 'major', label: 'Major', bg: 'danger' },
  { value: 'severe', label: 'Severe', bg: 'dark' },
];

const severityMeta = (value) => SEVERITIES.find((s) => s.value === value) || SEVERITIES[1];

const emptyForm = { id: null, drugAId: '', drugBId: '', severity: 'moderate', note: '' };

const DrugInteractionsManager = ({ token, drugItems = [] }) => {
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const result = await api.listDrugInteractions(token);
      setInteractions(Array.isArray(result?.interactions) ? result.interactions : []);
      setError('');
    } catch (err) {
      setError(err?.payload?.error || err?.message || 'Failed to load interactions.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const sortedDrugs = useMemo(() => (
    [...drugItems]
      .filter((d) => d && d.drugName)
      .sort((a, b) => (a.drugName || '').localeCompare(b.drugName || ''))
  ), [drugItems]);

  const filteredInteractions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return interactions;
    return interactions.filter((i) => (
      (i.drugAName || '').toLowerCase().includes(q)
      || (i.drugBName || '').toLowerCase().includes(q)
      || (i.note || '').toLowerCase().includes(q)
    ));
  }, [interactions, search]);

  const openCreate = () => {
    setForm(emptyForm);
    setModalError('');
    setShowModal(true);
  };

  const openEdit = (interaction) => {
    setForm({
      id: interaction.id,
      drugAId: interaction.drugAId,
      drugBId: interaction.drugBId,
      severity: interaction.severity,
      note: interaction.note || '',
    });
    setModalError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.id) {
      if (!form.drugAId || !form.drugBId || form.drugAId === form.drugBId) {
        setModalError('Pick two different drugs.');
        return;
      }
    }
    setSaving(true);
    try {
      if (form.id) {
        await api.updateDrugInteraction(form.id, {
          severity: form.severity,
          note: form.note,
        }, token);
      } else {
        await api.createDrugInteraction({
          drugAId: form.drugAId,
          drugBId: form.drugBId,
          severity: form.severity,
          note: form.note,
        }, token);
      }
      setShowModal(false);
      await refresh();
    } catch (err) {
      setModalError(err?.payload?.error || err?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await api.deleteDrugInteraction(confirmDeleteId, token);
      setConfirmDeleteId(null);
      await refresh();
    } catch (err) {
      setError(err?.payload?.error || err?.message || 'Delete failed.');
    }
  };

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h5 className="mb-1">Drug-drug interactions</h5>
            <div className="small text-muted">
              Curated pairs that warn students when both drugs end up on the same chart.
              Students must explicitly acknowledge each pair before submitting.
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Form.Control
              size="sm"
              placeholder="Search drug or note…"
              value={search}
              onChange={(ev) => setSearch(ev.target.value)}
              style={{ maxWidth: 240 }}
            />
            <Button variant="primary" size="sm" onClick={openCreate} disabled={sortedDrugs.length < 2}>
              <i className="bi bi-plus-lg me-1" /> New pair
            </Button>
          </div>
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        {loading ? (
          <div className="text-center my-4"><Spinner animation="border" /></div>
        ) : interactions.length === 0 ? (
          <Alert variant="info" className="mb-0">
            No interactions defined yet. Click <strong>New pair</strong> to add the first one.
          </Alert>
        ) : filteredInteractions.length === 0 ? (
          <Alert variant="info" className="mb-0">No pairs match your search.</Alert>
        ) : (
          <Table responsive bordered hover className="mb-0">
            <thead>
              <tr>
                <th>Drug A</th>
                <th>Drug B</th>
                <th>Severity</th>
                <th>Note</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredInteractions.map((i) => {
                const meta = severityMeta(i.severity);
                return (
                  <tr key={i.id}>
                    <td className="fw-semibold">{i.drugAName || '(deleted)'}</td>
                    <td className="fw-semibold">{i.drugBName || '(deleted)'}</td>
                    <td><Badge bg={meta.bg} text={meta.bg === 'warning' ? 'dark' : undefined}>{meta.label}</Badge></td>
                    <td className="small" style={{ whiteSpace: 'pre-wrap' }}>{i.note || <span className="text-muted fst-italic">No note</span>}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button size="sm" variant="outline-secondary" onClick={() => openEdit(i)}>
                          <i className="bi bi-pencil" />
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => setConfirmDeleteId(i.id)}>
                          <i className="bi bi-trash" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{form.id ? 'Edit interaction' : 'New interaction'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalError ? <Alert variant="danger">{modalError}</Alert> : null}
            {!form.id ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Drug A</Form.Label>
                  <Form.Select
                    value={form.drugAId}
                    onChange={(ev) => setForm((f) => ({ ...f, drugAId: ev.target.value }))}
                  >
                    <option value="">Select a drug…</option>
                    {sortedDrugs.map((d) => (
                      <option key={d.id} value={d.id} disabled={d.id === form.drugBId}>
                        {d.drugName}{d.strength ? ` ${d.strength}${d.unit || ''}` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Drug B</Form.Label>
                  <Form.Select
                    value={form.drugBId}
                    onChange={(ev) => setForm((f) => ({ ...f, drugBId: ev.target.value }))}
                  >
                    <option value="">Select a drug…</option>
                    {sortedDrugs.map((d) => (
                      <option key={d.id} value={d.id} disabled={d.id === form.drugAId}>
                        {d.drugName}{d.strength ? ` ${d.strength}${d.unit || ''}` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </>
            ) : null}
            <Form.Group className="mb-3">
              <Form.Label>Severity</Form.Label>
              <Form.Select
                value={form.severity}
                onChange={(ev) => setForm((f) => ({ ...f, severity: ev.target.value }))}
              >
                {SEVERITIES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Note (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={form.note}
                onChange={(ev) => setForm((f) => ({ ...f, note: ev.target.value }))}
                placeholder="Mechanism, clinical impact, or guidance shown to the student when the pair fires."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : (form.id ? 'Save changes' : 'Create pair')}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={!!confirmDeleteId} onHide={() => setConfirmDeleteId(null)}>
          <Modal.Header closeButton>
            <Modal.Title>Delete interaction?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Students will no longer be warned when both drugs end up on the same chart.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete}>Delete</Button>
          </Modal.Footer>
        </Modal>
      </Card.Body>
    </Card>
  );
};

export default DrugInteractionsManager;
